import { lazy, Suspense } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router-dom'

import ScrollToTop from './helpers/ScrollToTop'
import MyPackagesScreenPage from './PageComponents/ClientSide/MyPackagesScreen/MyPackagesScreenPage'
import PackageDetailsPage from './PageComponents/ClientSide/PackageDetailsPage/PackageDetailsPage'
import Payment from './PageComponents/ClientSide/Payment/Payment'
import PaymentSuccessful from './PageComponents/ClientSide/PaymentSuccessful/PaymentSuccessful'
import PaymentSuccessfulRP from './PageComponents/ClientSide/PaymentSuccessfulRP/PaymentSuccessfulRP'
import ReceiptUploadPage from './PageComponents/ClientSide/ReceiptUploadPage/ReceiptUploadPage'
import ReturnLabelUploadPage from './PageComponents/ClientSide/ReturnLabelUploadPage/ReturnLabelUploadPage'
import ReturnPackagePage from './PageComponents/ClientSide/ReturnPackagePage/ReturnPackagePage'
//ClientSide
import SignInComponent from './PageComponents/ClientSide/SignInComponent'
//OfficeSide
import AddNewPackagePage from './PageComponents/OfficeSide/AddNewPackage/AddNewPackagePage'
import AffiliatePage from './PageComponents/OfficeSide/Affiliate/AffiliatePage'
import AllPackagesPage from './PageComponents/OfficeSide/AllPackagesPage/AllPackagesPage.jsx'
import BarcodeGunShip from './PageComponents/OfficeSide/BarcodeGunShip/BarcodeGunShip'
import Coupon from './PageComponents/OfficeSide/Coupon/Coupon'
import { DelayedPayments } from './PageComponents/OfficeSide/DelayedPayments/DelayedPayments'
import EditAndUpdatePage from './PageComponents/OfficeSide/EditAndUpdate/EditAndUpdatePage'
import Location from './PageComponents/OfficeSide/Location/Location.jsx'
import CheckForm from './PageComponents/OfficeSide/PackageChecklist/checkForm'
import PackageRequests from './PageComponents/OfficeSide/PackageRequests/PackageRequests'
import PrintPackage from './PageComponents/OfficeSide/Print/printPackage'
import ReceiveShipments from './PageComponents/OfficeSide/RecieveShipments/ReceiveShipments'
import OfficeShipPageComponent from './PageComponents/OfficeSide/Ship/OfficeShipPageComponent'
import Stats from './PageComponents/OfficeSide/Stats/Stats'
import Sticker from './PageComponents/OfficeSide/Sticker/Sticker'
import { RoutePath } from './RoutePath'

const ClientPage = lazy(() => import('./PageComponents/v2/ClientPage'))
const ProfilePage = lazy(() => import('./PageComponents/v2/Profile/ProfilePage'))
const LoginPage = lazy(() => import('./PageComponents/v2/Login/LoginPage'))
const ForgotPasswordPage = lazy(() => import('./PageComponents/v2/Login/ForgotPasswordPage'))
const ClientPkgPage = lazy(() => import('./PageComponents/v2/ClientPkg/ClientPkgsPage'))
const ReturnPkgPage = lazy(() => import('./PageComponents/v2/ReturnPkg/ReturnPkgPage'))
const UnitePkgPage = lazy(() => import('./PageComponents/v2/UnitePkg/UnitePkgPage'))
const ReceiptUploadPageV2 = lazy(() => import('./PageComponents/v2/ReceiptUpload/ReceiptUploadPage'))
const PaymentPage = lazy(() => import('./PageComponents/v2/Payment/PaymentPage'))

const RouteWrapper = ({ Component, ...props }) => {
  return (
    <Suspense
      fallback={
        <div className='flex items-center justify-center' style={{ height: '100vh' }}>
          <div className='h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600' role='status'>
            <span className='sr-only'>Loading...</span>
          </div>
        </div>
      }
    >
      <Component {...props} />
    </Suspense>
  )
}

const ProtectedRoute = ({ children, v2, ...props }) => {
  const { loggedUser } = useSelector(state => state.user)
  return <Route {...props}>{loggedUser.Username ? children : <RouteWrapper Component={v2 ? LoginPage : SignInComponent} />}</Route>
}

export default function Layout() {
  const { loggedUser } = useSelector(state => state.user)

  return (
    <div className='layOutDiv'>
      <ScrollToTop />

      <Switch id='shiptanbul-switch'>
        <Route path='/welcome'>
          <SignInComponent />
        </Route>

        <Route path={RoutePath.Login}>
          <RouteWrapper Component={LoginPage} />
        </Route>

        <Route path={RoutePath.ForgotPassword}>
          <RouteWrapper Component={ForgotPasswordPage} />
        </Route>

        <ProtectedRoute v2 path={RoutePath.Client}>
          <RouteWrapper Component={ClientPage}>
            <Route exact path={RoutePath.ClientProfile}>
              <RouteWrapper Component={ProfilePage} />
            </Route>
            <Route path={`${RoutePath.ClientPkgReturn}/:pkgId`}>
              <RouteWrapper Component={ReturnPkgPage} />
            </Route>
            <Route exact path={RoutePath.ClientPkg}>
              <RouteWrapper Component={ClientPkgPage} />
            </Route>
            <Route exact path={RoutePath.ClientPkgUnite}>
              <RouteWrapper Component={UnitePkgPage} />
            </Route>
          </RouteWrapper>
        </ProtectedRoute>

        <ProtectedRoute v2 path={`${RoutePath.ReceiptUpload}/:pkgId`}>
          <RouteWrapper Component={ReceiptUploadPageV2} />
        </ProtectedRoute>

        <ProtectedRoute v2 path={`${RoutePath.PaymentShipping}/:pkgId`}>
          <RouteWrapper Component={PaymentPage} />
        </ProtectedRoute>

        <ProtectedRoute v2 path={`${RoutePath.PaymentSummary}/:pkgId`}>
          <RouteWrapper Component={PaymentPage} />
        </ProtectedRoute>

        <ProtectedRoute v2 path={`${RoutePath.PaymentProcess}/:pkgId`}>
          <RouteWrapper Component={PaymentPage} />
        </ProtectedRoute>

        <ProtectedRoute path='/personal-info'>
          <MyPackagesScreenPage />
        </ProtectedRoute>

        <ProtectedRoute path='/receipt-upload'>
          <ReceiptUploadPage />
        </ProtectedRoute>

        <ProtectedRoute path='/package-details'>
          <PackageDetailsPage />
        </ProtectedRoute>

        <ProtectedRoute path='/package-return-label'>
          <ReturnLabelUploadPage />
        </ProtectedRoute>

        <ProtectedRoute path='/payment'>
          <Payment />
        </ProtectedRoute>

        <ProtectedRoute path='/payment-successful:cfields?'>
          <PaymentSuccessful />
        </ProtectedRoute>

        <ProtectedRoute path='/rp-payment-successful'>
          <PaymentSuccessfulRP />
        </ProtectedRoute>

        <ProtectedRoute path='/return-package'>
          <ReturnPackagePage />
        </ProtectedRoute>

        <ProtectedRoute path='/office'>
          {(loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
            <AddNewPackagePage />
          )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-ship'>
          {(loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
            <OfficeShipPageComponent />
          )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-e&u'>
          {(loggedUser.permissions['Admin'] ||
            loggedUser.permissions['Agent'] ||
            loggedUser.permissions['OfficeManager'] ||
            loggedUser.permissions['SeniorAgent'] ||
            loggedUser.permissions['Worker']) && <EditAndUpdatePage />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-packageRequests/:reqType?/:reqId?'>
          {(loggedUser.permissions['Admin'] ||
            loggedUser.permissions['SeniorAgent'] ||
            loggedUser.permissions['Agent'] ||
            loggedUser.permissions['Worker'] ||
            loggedUser.permissions['OfficeManager']) && <PackageRequests />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-print'>
          {(loggedUser.permissions['Admin'] ||
            loggedUser.permissions['Agent'] ||
            loggedUser.permissions['SeniorAgent'] ||
            loggedUser.permissions['Worker'] ||
            loggedUser.permissions['OfficeManager']) && <PrintPackage />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-checkForm'>
          {(loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
            <CheckForm />
          )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-stats'>
          {(loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager']) && <Stats />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-affiliate'>{loggedUser.permissions['Admin'] && <AffiliatePage />}</ProtectedRoute>

        <ProtectedRoute path='/office-coupon'>{loggedUser.permissions['Admin'] && <Coupon />}</ProtectedRoute>

        <ProtectedRoute path='/office-receive-shipments'>
          {(loggedUser.permissions['Admin'] || loggedUser.permissions['Worker']) && <ReceiveShipments />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-delayed-payments'>
          {(loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
            <DelayedPayments />
          )}
        </ProtectedRoute>

        <ProtectedRoute path='/all-packages-view'>
          {(loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Worker']) && (
            <AllPackagesPage />
          )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-ship-barcode-gun'>
          {(loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Worker']) && (
            <BarcodeGunShip />
          )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-location'>
          {(loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Worker']) && <Location />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-sticker/:pId/:name/:unit/:brand/:address'>
          {(loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Worker']) && <Sticker />}
        </ProtectedRoute>

        <Route exact path='/'>
          <SignInComponent />
        </Route>
      </Switch>
    </div>
  )
}
